// Opening and saving a property document.
//
// The stored path is a fully-qualified CDN URL, so viewing is just a new tab —
// the same thing the agreements panel already does with its own file paths.
//
// Downloading is the awkward half. `<a download>` is IGNORED for a cross-origin
// href, and the CDN is a different origin from the app, so that attribute would
// silently open the file instead of saving it. Fetching the bytes and saving the
// blob is the only way to actually produce a file, and it needs CORS on the
// bucket. When that fetch does not work we open the tab rather than leaving a
// dead button — the seller still reaches their document, with one more step.
import { downloadBlob } from '@shared/utils'
import { logger } from '@core/logging/logger'
import type { PropertyDetail, PropertyDocument } from '../types/propertyTypes'

const FALLBACK_MESSAGE = 'Could not save the property document; opening it in a tab instead'

export function viewDocument(fileUrl: string): void {
  window.open(fileUrl, '_blank', 'noopener')
}

export async function downloadDocument(fileUrl: string, fileName: string): Promise<void> {
  try {
    const response = await fetch(fileUrl)
    if (response.ok) {
      downloadBlob(await response.blob(), fileName)
      return
    }
    logger.warn(FALLBACK_MESSAGE, { fileUrl, status: response.status })
  } catch (error) {
    // Almost always a CORS rejection rather than a missing file: the tab will
    // still render it, because a top-level navigation is not subject to CORS.
    logger.warn(FALLBACK_MESSAGE, { fileUrl, error })
  }
  viewDocument(fileUrl)
}

const RENT_AGREEMENT_TYPE = 'rent_agreement'

/**
 * The Documents tab's "Property documents": the record the server sends (title
 * deed, NOC…), plus the Ejari the seller uploaded with the request when the
 * property is let and ops have not filed one of their own. Marketing files and
 * the valuation report never arrive here — the server keeps them out.
 *
 * The request's Ejari carries no size or review status; the row shows neither.
 */
export function propertyRecordDocumentsOf(property: PropertyDetail): PropertyDocument[] {
  const { documents, rental } = property
  const hasFiledEjari = documents.some((document) => document.documentType === RENT_AGREEMENT_TYPE)
  if (hasFiledEjari || !rental?.ejariFileUrl) return documents
  const declaredEjari: PropertyDocument = {
    id: `ejari-${property.listingRequestId}`,
    documentType: RENT_AGREEMENT_TYPE,
    documentTitle: 'Ejari',
    fileUrl: rental.ejariFileUrl,
    fileName: rental.ejariFileName ?? 'ejari.pdf',
    fileSizeBytes: 0,
    mimeType: '',
    documentStatus: '',
    createdAt: '',
  }
  return [...documents, declaredEjari]
}
